import { ReactNode, useState, createContext, useContext, useMemo } from 'react'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { PinTokenType } from '../../../types/portalTypes/PinTokenType'

const JWT_TOKEN_NAME = 'pin_user_jwt_token'

const PinTokenContext = createContext<ReturnType<typeof usePinTokenValue> | undefined>(undefined)

function usePinTokenValue() {
  const [internalJWT, setInternalJWT] = useState<string | null>(() => {
    return Cookies.get(JWT_TOKEN_NAME) || null
  })
  
  const handleSetPinToken = (newPinToken: string | null) => {
    setInternalJWT(newPinToken)

    if (newPinToken) {
      const decoded = jwtDecode<PinTokenType>(newPinToken)
      const expiryDate = new Date(decoded.exp * 1000)

      Cookies.set(JWT_TOKEN_NAME, newPinToken, { 
        expires: expiryDate,
        sameSite: 'strict' 
      })
    } else {
      Cookies.remove(JWT_TOKEN_NAME)
    }
  }

  return useMemo(() => ({
    get pinToken() {
      return internalJWT
    },
    setPinToken: handleSetPinToken,
  }), [internalJWT])
}

export function usePinTokenState() {
  const context = useContext(PinTokenContext)
  if (!context) {
    throw new Error('usePinTokenState must be used within PinTokenStateProvider')
  }
  return context
}

export function PinTokenStateProvider({ children }: { children: ReactNode }) {
  const value = usePinTokenValue()
  return (
    <PinTokenContext.Provider value={value}>
      {children}
    </PinTokenContext.Provider>
  )
}

